import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Stack } from '@mui/material';
import ComparisonPeriodPicker from './ComparisonPeriodPicker';
import TimePeriodPicker from './TimePeriodPicker';
import { useDispatch, useSelector } from 'react-redux';
import { calendarActions } from '../../modules/reducer/calendarSlice';
import selectors from '../../modules/selectors/datePickerSelectors';
import {
  AUDIENCE_ANALYSIS,
  CHANNEL_OPPS,
  OVERALL_PERFORMANCE,
  OVERALL_PERFORMANCE_SUB_BRANCH,
  PAID_SEARCH,
} from '../../constants/datePickerConstants';

const DatePicker = ({
  component,
  setFilter,
  compare,
  combineCompare,
  restrictDataForPreviousYears = false,
}) => {
  const { currentQuarter: paidSearchSelectedQuarter, currentYear: paidSearchSelectedYear } =
    useSelector(selectors.paidSearchCombinedData);
  const {
    currentQuarter: overallPerformanceSelectedQuarter,
    currentYear: overallPerformanceSelectedYear,
    compareSelectedYear: overallPerformanceCompareSelectedYear,
    compareSelectedQuarter: overallPerformanceCompareSelectedQuarter,
  } = useSelector(selectors.overallPerformanceCombinedData);
  const {
    currentQuarter: overallPerformanceSubBranchSelectedQuarter,
    currentYear: overallPerformanceSubBranchSelectedYear,
    compareSelectedYear: overallPerformanceCompareSubBranchSelectedYear,
    compareSelectedQuarter: overallPerformanceCompareSubBranchSelectedQuarter,
  } = useSelector(selectors.overallPerformanceSubBranchCombinedData);
  const {
    currentQuarter: audienceAnalysisSelectedQuarter,
    currentYear: audienceAnalysisSelectedYear,
    compareSelectedQuarter: audienceAnalysisCompareSelectedQuarter,
    compareSelectedYear: audienceAnalysisCompareSelectedYear,
  } = useSelector(selectors.audienceAnalysisCombinedData);
  const { currentQuarter: channelOppsSelectedQuarter, currentYear: channelOppsSelectedYear } =
    useSelector(selectors.channeledOppsCombinedData);

  const dispatch = useDispatch();

  useEffect(() => {
    switch (component) {
      case PAID_SEARCH:
        dispatch(calendarActions.setCurrentQuarter(paidSearchSelectedQuarter));
        dispatch(calendarActions.setCurrentYear(paidSearchSelectedYear));
        break;
      case OVERALL_PERFORMANCE:
        dispatch(calendarActions.setCurrentQuarter(overallPerformanceSelectedQuarter));
        dispatch(calendarActions.setCurrentYear(overallPerformanceSelectedYear));
        dispatch(calendarActions.setComparisonQuarter(overallPerformanceCompareSelectedQuarter));
        dispatch(calendarActions.setComparisonYear(overallPerformanceCompareSelectedYear));
        break;
      case OVERALL_PERFORMANCE_SUB_BRANCH:
        dispatch(calendarActions.setCurrentQuarter(overallPerformanceSubBranchSelectedQuarter));
        dispatch(calendarActions.setCurrentYear(overallPerformanceSubBranchSelectedYear));
        dispatch(
          calendarActions.setComparisonQuarter(overallPerformanceCompareSubBranchSelectedQuarter)
        );
        dispatch(calendarActions.setComparisonYear(overallPerformanceCompareSubBranchSelectedYear));
        break;
      case AUDIENCE_ANALYSIS:
        dispatch(calendarActions.setCurrentQuarter(audienceAnalysisSelectedQuarter));
        dispatch(calendarActions.setCurrentYear(audienceAnalysisSelectedYear));
        dispatch(calendarActions.setComparisonQuarter(audienceAnalysisCompareSelectedQuarter));
        dispatch(calendarActions.setComparisonYear(audienceAnalysisCompareSelectedYear));
        break;
      case CHANNEL_OPPS:
        dispatch(calendarActions.setCurrentQuarter(channelOppsSelectedQuarter));
        dispatch(calendarActions.setCurrentYear(channelOppsSelectedYear));
        break;
      default:
    }
  }, [
    component,
    dispatch,
    paidSearchSelectedQuarter,
    paidSearchSelectedYear,
    overallPerformanceSelectedQuarter,
    overallPerformanceSelectedYear,
    audienceAnalysisSelectedYear,
    audienceAnalysisSelectedQuarter,
    audienceAnalysisCompareSelectedQuarter,
    audienceAnalysisCompareSelectedYear,
    overallPerformanceCompareSelectedQuarter,
    overallPerformanceCompareSelectedYear,
    channelOppsSelectedQuarter,
    channelOppsSelectedYear,
    overallPerformanceSubBranchSelectedQuarter,
    overallPerformanceSubBranchSelectedYear,
    overallPerformanceCompareSubBranchSelectedQuarter,
    overallPerformanceCompareSubBranchSelectedYear,
  ]);

  return (
    <Stack spacing={1} direction="row" style={{ display: 'flex', alignItems: 'center' }}>
      <TimePeriodPicker
        period={setFilter}
        component={component}
        restrictDataForPreviousYears={restrictDataForPreviousYears}
      />
      {combineCompare && (
        <ComparisonPeriodPicker
          combine={combineCompare}
          comparePeriod={compare}
          component={component}
          combineCompare={combineCompare}
        />
      )}
    </Stack>
  );
};

// Prop validation
DatePicker.propTypes = {
  component: PropTypes.element,
  setFilter: PropTypes.func,
  compare: PropTypes.object,
  combineCompare: PropTypes.bool,
  restrictDataForPreviousYears: PropTypes.bool,
};

export default DatePicker;
